import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Organization,
  OrganizationId,
  ViewContext,
  TabId,
  DEFAULT_ORGANIZATIONS,
  ORGANIZATION_ICONS,
  ORGANIZATION_COLORS,
} from './types'
import { getNexusClient, createOrgClient } from '@/lib/supabase'
import { SupabaseClient } from '@supabase/supabase-js'
import { OrganizationRow } from '@/lib/supabase/types'

interface OrgContextType {
  // Current state
  currentOrg: ViewContext
  currentTab: TabId
  organizations: Organization[]
  isLoading: boolean
  selectedProjectId: string | 'all'

  // Navigation
  setCurrentOrg: (org: ViewContext) => void
  setCurrentTab: (tab: TabId) => void
  setSelectedProjectId: (projectId: string | 'all') => void

  // Helpers
  isGlobalView: boolean
  activeOrg: Organization | null
  getOrgClient: (orgId: OrganizationId) => SupabaseClient | null
  buyrClient: SupabaseClient | null // Quick access to Buy'r client

  // Organization management
  refreshOrganizations: () => Promise<void>
}

const OrgContext = createContext<OrgContextType | undefined>(undefined)

const STORAGE_KEY_ORG = 'nexus-current-org'
const STORAGE_KEY_TAB = 'nexus-current-tab'
const STORAGE_KEY_PROJECT = 'nexus-selected-project'

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [organizations, setOrganizations] = useState<Organization[]>(DEFAULT_ORGANIZATIONS)
  const [isLoading, setIsLoading] = useState(true)
  const [orgClients, setOrgClients] = useState<Map<OrganizationId, SupabaseClient>>(new Map())

  // Get current org/tab from URL or localStorage
  const currentOrg = useMemo<ViewContext>(() => {
    const urlOrg = searchParams.get('org') as ViewContext | null
    if (urlOrg && (urlOrg === 'global' || organizations.some((o) => o.slug === urlOrg))) {
      return urlOrg
    }
    const stored = localStorage.getItem(STORAGE_KEY_ORG) as ViewContext | null
    return stored || 'global'
  }, [searchParams, organizations])

  const currentTab = useMemo<TabId>(() => {
    const urlTab = searchParams.get('tab') as TabId | null
    if (urlTab) return urlTab
    const stored = localStorage.getItem(STORAGE_KEY_TAB) as TabId | null
    return stored || 'overview'
  }, [searchParams])

  const selectedProjectId = useMemo<string | 'all'>(() => {
    const urlProject = searchParams.get('project')
    if (urlProject) return urlProject
    const stored = localStorage.getItem(STORAGE_KEY_PROJECT)
    return stored || 'all'
  }, [searchParams])

  const isGlobalView = currentOrg === 'global'

  const activeOrg = useMemo(() => {
    if (isGlobalView) return null
    return organizations.find((o) => o.slug === currentOrg) || null
  }, [currentOrg, isGlobalView, organizations])

  // Fetch organizations from Nexus Admin
  const refreshOrganizations = useCallback(async () => {
    setIsLoading(true)
    try {
      const nexus = getNexusClient()
      const { data, error } = await nexus.from('organizations').select('*').order('name').returns<OrganizationRow[]>()

      if (error) {
        console.error('Failed to fetch organizations:', error)
        return
      }

      if (data) {
        const orgs: Organization[] = data.map((org: OrganizationRow) => {
          // Check if we have env var fallback for this org
          let isConfigured = org.is_configured
          let isActive = org.is_active
          if (!isConfigured && org.slug === 'buyr') {
            isConfigured = !!(import.meta.env.VITE_BUYR_SUPABASE_URL && import.meta.env.VITE_BUYR_SUPABASE_ANON_KEY)
          }
          if (org.slug === 'webb') {
            const hasWebbEnv = !!(import.meta.env.VITE_WEBB_SUPABASE_URL && import.meta.env.VITE_WEBB_SUPABASE_ANON_KEY)
            if (!isConfigured) isConfigured = hasWebbEnv
            if (!isActive) isActive = hasWebbEnv
          }

          return {
            id: org.id,
            slug: org.slug as OrganizationId,
            name: org.name,
            icon: ORGANIZATION_ICONS[org.slug as OrganizationId],
            color: ORGANIZATION_COLORS[org.slug as OrganizationId] || 'text-gray-400',
            description: org.description || '',
            isActive,
            isConfigured,
          }
        }).filter((o) => ['buyr', 'webb'].includes(o.slug))
        setOrganizations(orgs)

        // Create clients for configured orgs
        const clients = new Map<OrganizationId, SupabaseClient>()
        const orgsWithEnvFallback = data.filter((o: OrganizationRow) => {
          if (o.is_configured || o.is_active) return true
          // Include orgs that have env var credentials even if DB says inactive
          if (o.slug === 'webb' && import.meta.env.VITE_WEBB_SUPABASE_URL && import.meta.env.VITE_WEBB_SUPABASE_ANON_KEY) return true
          if (o.slug === 'buyr' && import.meta.env.VITE_BUYR_SUPABASE_URL && import.meta.env.VITE_BUYR_SUPABASE_ANON_KEY) return true
          return false
        })
        for (const org of orgsWithEnvFallback) {
          // Get credentials from DB or fall back to env vars
          let supabaseUrl = org.supabase_url
          let supabaseAnonKey = org.supabase_anon_key

          // Fallback to env vars for known orgs during development
          if (!supabaseUrl || !supabaseAnonKey) {
            if (org.slug === 'buyr') {
              supabaseUrl = import.meta.env.VITE_BUYR_SUPABASE_URL
              supabaseAnonKey = import.meta.env.VITE_BUYR_SUPABASE_ANON_KEY
            }
            if (org.slug === 'webb') {
              supabaseUrl = import.meta.env.VITE_WEBB_SUPABASE_URL
              supabaseAnonKey = import.meta.env.VITE_WEBB_SUPABASE_ANON_KEY
            }
          }

          if (supabaseUrl && supabaseAnonKey) {
            const client = createOrgClient({
              id: org.id,
              slug: org.slug as OrganizationId,
              name: org.name,
              icon: org.icon,
              color: org.color,
              description: org.description || '',
              supabaseUrl,
              supabaseAnonKey,
              isActive: org.is_active,
              isConfigured: true, // Mark as configured since we have credentials
            })
            if (client) {
              clients.set(org.slug as OrganizationId, client)
            }
          }
        }
        setOrgClients(clients)
      }
    } catch (err) {
      console.error('Error fetching organizations:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Load organizations on mount
  useEffect(() => {
    refreshOrganizations()
  }, [refreshOrganizations])

  const setCurrentOrg = useCallback(
    (org: ViewContext) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('org', org)
      newParams.set('tab', 'overview') // Reset to overview when switching orgs
      setSearchParams(newParams)
      localStorage.setItem(STORAGE_KEY_ORG, org)
      localStorage.setItem(STORAGE_KEY_TAB, 'overview')
    },
    [searchParams, setSearchParams]
  )

  const setCurrentTab = useCallback(
    (tab: TabId) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('tab', tab)
      setSearchParams(newParams)
      localStorage.setItem(STORAGE_KEY_TAB, tab)
    },
    [searchParams, setSearchParams]
  )

  const setSelectedProjectId = useCallback(
    (projectId: string | 'all') => {
      const newParams = new URLSearchParams(searchParams)
      if (projectId === 'all') {
        newParams.delete('project')
      } else {
        newParams.set('project', projectId)
      }
      setSearchParams(newParams)
      localStorage.setItem(STORAGE_KEY_PROJECT, projectId)
    },
    [searchParams, setSearchParams]
  )

  const getOrgClient = useCallback(
    (orgId: OrganizationId): SupabaseClient | null => {
      return orgClients.get(orgId) || null
    },
    [orgClients]
  )

  // Quick access to Buy'r client (the main active org)
  const buyrClient = useMemo(() => {
    return orgClients.get('buyr') || null
  }, [orgClients])

  return (
    <OrgContext.Provider
      value={{
        currentOrg,
        currentTab,
        organizations,
        isLoading,
        selectedProjectId,
        setCurrentOrg,
        setCurrentTab,
        setSelectedProjectId,
        isGlobalView,
        activeOrg,
        getOrgClient,
        buyrClient,
        refreshOrganizations,
      }}
    >
      {children}
    </OrgContext.Provider>
  )
}

export function useOrg() {
  const context = useContext(OrgContext)
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider')
  }
  return context
}
